/**
 * Smart private-note form — floating fields, live readiness, portal API.
 */

import { portalUrl } from "./analytics.js";

const PERSON = "Confirm you are a person, then send.";
const PERSON_MISSING = "The person check did not load. Refresh and try again.";

function isEmail(v) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

export function initContactForm() {
  const form = document.getElementById("contact-form");
  if (!form) return null;

  const name = form.querySelector('input[name="name"]');
  const email = form.querySelector('input[name="email"]');
  const message = form.querySelector('textarea[name="message"]');
  const send = form.querySelector(".note-form__send");
  const status = form.querySelector("#contact-form-status");
  const count = form.querySelector("[data-note-count]");
  const fields = [...form.querySelectorAll(".field")];
  const turnstileEl = form.querySelector("[data-turnstile]");
  const sitekey = turnstileEl?.dataset.sitekey || "";
  let widgetId = null;

  if (!name || !email || !message || !send) return null;

  form.action = portalUrl("/api/contact");

  function turnstileToken() {
    const input = form.querySelector('[name="cf-turnstile-response"]');
    return (input?.value || "").trim();
  }

  function mountTurnstile() {
    if (!sitekey || !window.turnstile || !turnstileEl || widgetId !== null) return;
    try {
      widgetId = window.turnstile.render(turnstileEl, {
        sitekey,
        action: "contact",
        theme: "light",
        appearance: "interaction-only",
        callback() { refresh(); },
        "expired-callback"() { refresh(); },
        "error-callback"() { refresh(); },
      });
    } catch {
      widgetId = null;
    }
  }

  function watchTurnstile(tries = 0) {
    if (!sitekey) return;
    if (window.turnstile?.render) {
      mountTurnstile();
      return;
    }
    if (tries > 80) return;
    window.setTimeout(() => watchTurnstile(tries + 1), 50);
  }

  function resetTurnstile() {
    if (widgetId !== null && window.turnstile) window.turnstile.reset(widgetId);
  }

  function setFilled(field, el) {
    field.classList.toggle("is-filled", Boolean(el.value.trim()));
  }

  function syncField(field) {
    const el = field.querySelector("input, textarea");
    if (!el) return;
    setFilled(field, el);
    const bad =
      field.classList.contains("is-touched") &&
      el.required &&
      (el.type === "email" ? !isEmail(el.value.trim()) : !el.value.trim());
    field.classList.toggle("is-invalid", bad);
    field.classList.toggle("is-valid", el.value.trim() && !bad);
  }

  function ready() {
    return (
      name.value.trim().length >= 2 &&
      isEmail(email.value.trim()) &&
      message.value.trim().length >= 12
    );
  }

  function refresh() {
    fields.forEach(syncField);
    if (count) count.textContent = String(message.value.trim().length);
    const ok = ready();
    send.disabled = !ok || form.classList.contains("is-sending");
    form.classList.toggle("is-ready", ok);
  }

  fields.forEach((field) => {
    const el = field.querySelector("input, textarea");
    if (!el) return;
    el.addEventListener("focus", () => field.classList.add("is-focus"));
    el.addEventListener("blur", () => {
      field.classList.remove("is-focus");
      field.classList.add("is-touched");
      syncField(field);
    });
    el.addEventListener("input", refresh);
  });

  function showStatus(text, kind = "") {
    if (!status) return;
    status.hidden = !text;
    status.textContent = text;
    status.classList.toggle("is-ok", kind === "ok");
    status.classList.toggle("is-err", kind === "err");
  }

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    fields.forEach((f) => f.classList.add("is-touched"));
    refresh();
    if (!ready() || form.classList.contains("is-sending")) return;

    if (sitekey && !window.turnstile) {
      showStatus(PERSON_MISSING, "err");
      return;
    }

    if (sitekey && !turnstileToken()) {
      showStatus(PERSON, "err");
      return;
    }

    form.classList.add("is-sending");
    send.disabled = true;
    showStatus("Sending…");

    try {
      const body = new FormData(form);
      const res = await fetch(form.action, {
        method: "POST",
        body,
        headers: { Accept: "application/json" },
        mode: "cors",
      });

      if (!res.ok) throw new Error("send-failed");

      form.classList.remove("is-sending");
      form.classList.add("is-sent");
      showStatus("Received. We’ll reply personally.", "ok");
      form.reset();
      resetTurnstile();
      fields.forEach((f) => {
        f.classList.remove("is-touched", "is-valid", "is-invalid", "is-filled");
      });
      refresh();
      send.disabled = true;

      window.setTimeout(() => {
        form.classList.remove("is-sent");
      }, 4200);
    } catch {
      form.classList.remove("is-sending");
      showStatus("Couldn’t send — try again in a moment.", "err");
      resetTurnstile();
      refresh();
    }
  });

  watchTurnstile();
  refresh();
  return form;
}
